/**
 * Deterministic answers for the questions landlords ask most. The backend
 * model is tiny and can garble numbers, so for these we compute the answer
 * straight from the snapshot: instant, exact, and identical every time.
 * Anything advisory or open-ended ("how can I…", "should I…", "draft…") is
 * left to the LLM, which still gets the same facts as context.
 */
import { formatDate, formatNaira, joinList, plural } from './format';
import { navAction, type NavAction } from './navTargets';
import type { PortfolioSnapshot } from './types';

export type IntentId =
  | 'summary'
  | 'overdue'
  | 'collection'
  | 'leases'
  | 'plots'
  | 'payments'
  | 'leads'
  | 'proposals'
  | 'schedule';

export interface IntentAnswer {
  content: string;
  actions: NavAction[];
}

const ADVISORY_RE =
  /\b(should|recommend|advi[cs]e|suggest|best|why|tips?|draft|write|compose|explain|improve|reduce|increase|plant|grow|what if)\b|\bhow (do|can|could|should|would) (i|we)\b/i;

// Order matters: the first match wins.
const INTENT_RULES: Array<[RegExp, IntentId]> = [
  [/\b(summary|summari[sz]e|overview|brief me|catch me up|how('s| is| are) (my|the) (portfolio|business|farms?|plots|things)( doing)?)\b|\bhow am i doing\b/i, 'summary'],
  [/\b(overdue|owes?|owing|arrears|unpaid|late pay|defaulting|debtors?|hasn'?t paid|haven'?t paid|not paid)\b/i, 'overdue'],
  [/\b(collection rate|collected|collecting|how much (have i|did i|i've) (made|earned|received)|revenue|income)\b/i, 'collection'],
  [/\b(leases?|contracts?|tenanc(y|ies))\b.*\b(end|ending|expir\w*|renew\w*|due|finish\w*)\b|\b(expir\w*|ending soon|renew\w*)\b/i, 'leases'],
  [/\b(how many plots|number of plots|plot count|my plots|vacant|vacancy|empty plots?|occupan\w*|occupied)\b/i, 'plots'],
  [/\b(payments?|paid|received)\b.*\b(recent|this month|last month|last 30|30 days|lately|week)\b|\brecent payments?\b/i, 'payments'],
  [/\b(leads?|enquir\w*|inquir\w*)\b/i, 'leads'],
  [/\bproposals?\b/i, 'proposals'],
  [/\b(schedule|calendar|upcoming (tasks?|activit\w*|events?)|this week|what'?s next|agenda)\b/i, 'schedule'],
];

/** The data topic a question is about, even if it is advisory ("…what should I do?"). */
export function detectTopic(question: string): IntentId | null {
  const q = question.trim();
  if (!q) return null;
  for (const [re, intent] of INTENT_RULES) {
    if (re.test(q)) return intent;
  }
  return null;
}

/** A question we can answer exactly from data; advisory questions go to the model. */
export function detectIntent(question: string): IntentId | null {
  if (ADVISORY_RE.test(question)) return null;
  return detectTopic(question);
}

function plotsAnswer(s: PortfolioSnapshot): IntentAnswer {
  const p = s.plots;
  if (p.total === 0) {
    return { content: "You haven't added any plots yet. Add your first plot to start tracking occupancy.", actions: [navAction('plots')] };
  }
  const lines = [`You have **${plural(p.total, 'plot')}**: ${p.occupied} occupied and ${p.vacant} vacant, so occupancy is **${p.occupancyRate}%**.`];
  if (p.vacantPlots.length) {
    lines.push('', 'Vacant and ready to let:', ...p.vacantPlots.slice(0, 6).map((v) => `- **${v.title}**${v.size ? ` (${v.size})` : ''}`));
  } else {
    lines.push('', 'Every plot is let. Nice work.');
  }
  if (p.archived) lines.push('', `${plural(p.archived, 'archived plot')} not counted.`);
  return { content: lines.join('\n'), actions: [navAction('plots')] };
}

function overdueAnswer(s: PortfolioSnapshot): IntentAnswer {
  const i = s.invoices;
  if (i.overdue.length === 0) {
    return { content: 'Good news: nothing is overdue right now. Every invoice past its due date has been paid.', actions: [navAction('invoices')] };
  }
  const tenants = i.overdueByTenant.slice(0, 6).map(
    (t) => `- **${t.tenant}**: ${formatNaira(t.balance)} across ${plural(t.invoiceCount, 'invoice')}, oldest ${t.oldestDaysOverdue} days late`,
  );
  const more = i.overdueByTenant.length > 6 ? [`- …and ${plural(i.overdueByTenant.length - 6, 'more tenant')}`] : [];
  const oldest = i.overdue[0];
  const oldestLine = oldest
    ? `The oldest is ${oldest.invoiceNumber}${oldest.plot ? ` on ${oldest.plot}` : ''}, due ${formatDate(oldest.dueDate)}.`
    : '';
  return {
    content: [
      `**${formatNaira(i.overdueTotal)}** is overdue across ${plural(i.overdue.length, 'invoice')} from ${plural(i.overdueByTenant.length, 'tenant')}:`,
      ...tenants,
      ...more,
      '',
      oldestLine,
    ].join('\n'),
    actions: [navAction('invoices'), navAction('payments')],
  };
}

function collectionAnswer(s: PortfolioSnapshot): IntentAnswer {
  const i = s.invoices;
  if (i.count === 0 || i.collectionRate === null) {
    return { content: "There are no invoices yet, so there's no collection rate to report.", actions: [navAction('invoices')] };
  }
  return {
    content: [
      `Your collection rate is **${i.collectionRate}%**: you've collected ${formatNaira(i.collected)} of ${formatNaira(i.billed)} billed across ${plural(i.count, 'invoice')}.`,
      '',
      `${formatNaira(i.outstanding)} is still unpaid, ${
        i.overdueTotal >= i.outstanding ? 'and all of it is overdue' : `of which ${formatNaira(i.overdueTotal)} is overdue`
      }. In the last 30 days you received ${formatNaira(s.payments.last30DaysTotal)}.`,
    ].join('\n'),
    actions: [navAction('payments'), navAction('invoices')],
  };
}

function leasesAnswer(s: PortfolioSnapshot): IntentAnswer {
  const l = s.leases;
  if (l.endingSoon.length === 0) {
    return {
      content: `No leases end in the next ${l.windowDays} days. You have ${plural(l.active, 'active lease')} and ${l.upcoming} upcoming.`,
      actions: [navAction('contracts')],
    };
  }
  return {
    content: [
      `**${plural(l.endingSoon.length, 'lease')}** end in the next ${l.windowDays} days:`,
      ...l.endingSoon.slice(0, 6).map((e) => `- **${e.plot || 'A plot'}** (${e.tenant}): ends ${formatDate(e.endDate)}, in ${plural(e.daysLeft, 'day')}`),
      '',
      'Now is a good time to agree renewals or line up new tenants.',
    ].join('\n'),
    actions: [navAction('contracts')],
  };
}

function paymentsAnswer(s: PortfolioSnapshot): IntentAnswer {
  const p = s.payments;
  const content = p.last30DaysCount
    ? `In the last 30 days you received **${formatNaira(p.last30DaysTotal)}** from ${plural(p.last30DaysCount, 'payment')}.`
    : 'No payments were recorded in the last 30 days.';
  return { content, actions: [navAction('payments')] };
}

function leadsAnswer(s: PortfolioSnapshot): IntentAnswer {
  const l = s.leads;
  const content = l.total
    ? `You have **${plural(l.total, 'lead')}**, ${l.unread} unread.${l.unread ? ' Reply quickly: fast responses win tenants.' : ''}`
    : 'No leads yet. Share your plot listings to start getting enquiries.';
  return { content, actions: [navAction('leads')] };
}

function proposalsAnswer(s: PortfolioSnapshot): IntentAnswer {
  const p = s.proposals;
  const content = p.total
    ? `You have **${plural(p.total, 'proposal')}**: ${p.inReview} in review and ${p.approved} approved.`
    : 'There are no proposals yet.';
  return { content, actions: [navAction('proposals')] };
}

function scheduleAnswer(s: PortfolioSnapshot): IntentAnswer {
  const up = s.schedule.upcoming;
  const content = up.length
    ? [`Coming up in the next ${s.schedule.windowDays} days:`, ...up.slice(0, 6).map((a) => `- **${a.title}** on ${formatDate(a.date)}`)].join('\n')
    : `Nothing is scheduled for the next ${s.schedule.windowDays} days. You have ${plural(s.schedule.total, 'activity', 'activities')} in total.`;
  return { content, actions: [navAction('schedule')] };
}

function summaryAnswer(s: PortfolioSnapshot): IntentAnswer {
  const lines = ["Here's where things stand:"];
  if (s.plots.total) {
    lines.push(`- **Plots:** ${s.plots.occupied} of ${s.plots.total} let (${s.plots.occupancyRate}% occupancy), ${s.plots.vacant} vacant`);
  }
  if (s.invoices.count) {
    lines.push(`- **Collections:** ${s.invoices.collectionRate}% collected, ${formatNaira(s.invoices.overdueTotal)} overdue`);
  }
  const ending = s.leases.endingSoon;
  const next = ending[0];
  lines.push(
    next
      ? `- **Leases:** ${plural(ending.length, 'lease')} ending within ${s.leases.windowDays} days (next: ${next.plot || 'a plot'}, ${formatDate(next.endDate)})`
      : `- **Leases:** ${s.leases.active} active, none ending within ${s.leases.windowDays} days`,
  );
  if (s.leads.unread) lines.push(`- **Leads:** ${s.leads.unread} unread`);
  const priorities: string[] = [];
  const topDebtor = s.invoices.overdueByTenant[0];
  if (topDebtor) priorities.push(`chase ${topDebtor.tenant}`);
  if (next) priorities.push(`renew ${next.plot || 'the next lease'}`);
  if (s.plots.vacant) priorities.push(`let ${s.plots.vacantPlots[0]?.title ?? 'your vacant plots'}`);
  if (priorities.length) lines.push('', `Top priorities: ${joinList(priorities)}.`);
  const actions = s.invoices.overdue.length ? [navAction('invoices'), navAction('dashboard')] : [navAction('dashboard')];
  return { content: lines.join('\n'), actions };
}

const ANSWERS: Record<IntentId, (s: PortfolioSnapshot) => IntentAnswer> = {
  summary: summaryAnswer,
  overdue: overdueAnswer,
  collection: collectionAnswer,
  leases: leasesAnswer,
  plots: plotsAnswer,
  payments: paymentsAnswer,
  leads: leadsAnswer,
  proposals: proposalsAnswer,
  schedule: scheduleAnswer,
};

export function answerIntent(intent: IntentId, snapshot: PortfolioSnapshot): IntentAnswer {
  return ANSWERS[intent](snapshot);
}
